import * as React from 'react';
import { Dialog, IconButton, styled } from '@mend/mui';
import InfoIcon from '@mui/icons-material/Info';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Box, Button, Card, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router';

import { useNavigationStore } from '#stores/navigation.ts';

// Size options for widgets
export type WidgetSize = 'half' | 'full';

interface StyledCardProps {
  size: WidgetSize;
}

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'size',
})<StyledCardProps>(({ size, theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  gridColumn: size === 'full' ? '1 / -1' : 'auto', // Span all columns if full size
}));

interface BaseProps {
  /**
   * The ID of the widget. It is passed to the `data-testid` attribute of the
   * root element.
   */
  id?: string;
  title: string;
  infoTemplate?: React.ReactNode;
  description?: string;
  children: React.ReactNode | ((isOpenedInDialog: boolean) => React.ReactNode);
  size?: WidgetSize;
  hideExpandButton?: boolean;
  drilldownRoutePath?: string;
}

/**
 * Base component for all dashboard widgets
 */
export function Base(props: BaseProps) {
  const {
    id,
    title,
    drilldownRoutePath,
    infoTemplate,
    description,
    children,
    size = 'half',
    hideExpandButton = false,
  } = props;
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isInfoDialogOpen, setIsInfoDialogOpen] = React.useState(false);

  const location = useLocation();
  const setNavigationState = useNavigationStore((state) => state.setState);

  const handleCloseInfoDialog = () => {
    setIsInfoDialogOpen(false);
  };

  // Generate unique IDs for accessibility
  const titleId = React.useId();
  const descriptionId = React.useId();

  return (
    <>
      <StyledCard size={size} data-testid={id}>
        <Box mb={2}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ width: '100%', overflow: 'hidden' }}
            >
              <Typography
                component="h2"
                variant="h6"
                sx={{
                  flexShrink: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  pr: 0.5,
                }}
              >
                {title}
              </Typography>

              {infoTemplate && (
                <Tooltip title="Info" arrow>
                  <IconButton
                    onClick={() => setIsInfoDialogOpen(true)}
                    size="small"
                    sx={{ flexShrink: 0 }}
                    aria-label="Open info"
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {!hideExpandButton && (
              <Tooltip title="Expand" arrow>
                <IconButton
                  onClick={() => setIsDialogOpen(true)}
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>

        <Box>{typeof children === 'function' ? children(false) : children}</Box>

        {drilldownRoutePath && (
          <Stack justifyContent="flex-end" mt={2}>
            <Button
              component={RouterLink}
              size="small"
              color="info"
              variant="text"
              to={drilldownRoutePath}
              onClick={() => setNavigationState('/', location.search)}
              aria-label={`View ${title} details`}
            >
              View Details
            </Button>
          </Stack>
        )}
      </StyledCard>

      {infoTemplate && isInfoDialogOpen && (
        <Dialog.Root open onClose={handleCloseInfoDialog} fullWidth>
          <Dialog.Header>
            <Dialog.Title>{title}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Content>{infoTemplate}</Dialog.Content>
        </Dialog.Root>
      )}

      {isDialogOpen && (
        <Dialog.Root
          open
          onClose={() => setIsDialogOpen(false)}
          fullWidth
          fullHeight
          maxWidth="xl"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
        >
          <Dialog.Header>
            <Dialog.Title id={titleId}>{title}</Dialog.Title>
            {description && (
              <Dialog.Description id={descriptionId}>
                {description}
              </Dialog.Description>
            )}
          </Dialog.Header>
          <Dialog.Content>
            {typeof children === 'function' ? children(true) : children}
          </Dialog.Content>
        </Dialog.Root>
      )}
    </>
  );
}
